import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons/faCircleInfo";
import { faRotateLeft } from "@fortawesome/free-solid-svg-icons/faRotateLeft";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons/faTriangleExclamation";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { Entry } from "../../../model/entry/Entry";
import { Spacing } from "../../../theme/Spacing";
import { Tooltip } from "../../../utils/local-antd";
import {
  DuplicateEntryFieldDescriptor,
  withEntryFieldValue,
} from "../utils/duplicateEntryFields";
import { CustomFieldEditableCell } from "./CustomFieldEditableCell";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  GhostButton,
} from "./DuplicateCardPrimitives";
import { EntryKindBadge } from "./EntryKindBadge";
import { EntryMetaStrip } from "./EntryMetaStrip";
import { EntryPreviewButton } from "./EntryPreviewButton";
import { MarkedForDeletionBadge } from "./MarkedForDeletionBadge";
import { TestIDs } from "../../../tests/types/testIds";
import { isBlankValue } from "../../../utils/isBlankValue";

const KeepHeader = styled(CardHeader)`
  padding: ${Spacing.s} ${Spacing.m};
`;

const KeepBody = styled(CardBody)`
  padding: ${Spacing.s} ${Spacing.m};
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(13rem, 1fr));
  gap: ${Spacing.s} ${Spacing.m};
`;

const NoticeFooter = styled(CardFooter)`
  align-items: flex-start;
  padding: ${Spacing.s} ${Spacing.m};
  background-color: ${({ theme }) => theme.lightSub};
`;

type Props = {
  entry: Entry;
  otherEntries: Entry[];
  descriptors: DuplicateEntryFieldDescriptor[];
  isModified: boolean;
  autoPickedReason?: string;
  onUpdate: ATVoidFunction<Partial<Entry>>;
  onReset: VoidFunction;
  onPreview: ATVoidFunction<string>;
};

export const EntryKeepCard: React.FC<Props> = ({
  entry,
  otherEntries,
  descriptors,
  isModified,
  autoPickedReason,
  onUpdate,
  onReset,
  onPreview,
}) => {
  const { t } = useTranslation();

  const visibleFields = descriptors.filter(
    (descriptor) =>
      descriptor.actionable &&
      (!isBlankValue(descriptor.getValue(entry)) ||
        otherEntries.some(
          (other) => !isBlankValue(descriptor.getValue(other)),
        )),
  );

  return (
    <Card $accent data-testid={TestIDs.entries.duplicates.resolve.keptCard}>
      <KeepHeader>
        <Flex flexDirection="column" gap="xs" minWidth={0}>
          <Flex alignItems="center" gap="m" flexWrap="wrap">
            <Flex alignItems="center" gap="s">
              <AtiraIcon icon={faCheck} color="main" />
              <Text fontSize="m" fontWeight="bold" color="main">
                {t("entries.duplicates.keepCard.title")}
              </Text>
              <EntryKindBadge kind={entry.kind} />
              {entry.markedForDeletion ? <MarkedForDeletionBadge /> : null}
              {autoPickedReason ? (
                <Tooltip title={autoPickedReason}>
                  <span>
                    <AtiraIcon icon={faCircleInfo} color="darkerSub" />
                  </span>
                </Tooltip>
              ) : null}
            </Flex>

            <Text fontSize="s" color="darkerSub">
              {t("entries.duplicates.keepCard.subtitle")}
            </Text>
          </Flex>

          <EntryMetaStrip entry={entry} descriptors={descriptors} />
        </Flex>

        <Flex alignItems="center" gap="s">
          {isModified ? (
            <GhostButton fontSize="xs" icon={faRotateLeft} onClick={onReset}>
              {t("entries.duplicates.keepCard.reset")}
            </GhostButton>
          ) : null}
          <EntryPreviewButton entryId={entry._id} onPreview={onPreview} />
        </Flex>
      </KeepHeader>

      <KeepBody>
        <FieldsGrid>
          {visibleFields.map((descriptor) => (
            <Flex key={descriptor.key} flexDirection="column" gap="xs">
              <Text fontSize="xs" color="darkerSub">
                {descriptor.label}
              </Text>

              {/* The cell edits `fields` only, so it gets the value through them
                  and its saves go back to wherever the value really lives */}
              <CustomFieldEditableCell
                entry={{
                  _id: entry._id,
                  fields: { [descriptor.key]: descriptor.getValue(entry) },
                }}
                field={descriptor.key}
                type={descriptor.resolveType(entry)}
                onUpdate={(updated) =>
                  onUpdate(
                    withEntryFieldValue(
                      entry,
                      descriptor.key,
                      updated.fields?.[descriptor.key] ?? "",
                    ),
                  )
                }
                active
                commitOnChange
              />
            </Flex>
          ))}
        </FieldsGrid>
      </KeepBody>

      <NoticeFooter>
        <AtiraIcon icon={faTriangleExclamation} color="warning" />
        <Text fontSize="s">{t("entries.duplicates.keepCard.notice")}</Text>
      </NoticeFooter>
    </Card>
  );
};
